import { encoder, BytesEncoder, RpcResponse, DateEncoder, RecordMetadata, SmartContractType, RpcRequestData, TezosDomainsValidator, createTracer, AddressBook, UnsupportedDomainNameValidator } from '@tezos-domains/core';
import { createResolver, NullNameResolver } from '@tezos-domains/resolver';
import { CommitmentGenerator, TaquitoManagerDataProvider, TaquitoTezosDomainsOperationFactory, BlockchainDomainsManager, UnsupportedDomainsManager } from '@tezos-domains/manager';
import { MapEncoder, BigNumberEncoder, TaquitoClient } from '@tezos-domains/taquito';

/*! *****************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */

function __decorate(decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
}

function __metadata(metadataKey, metadataValue) {
    if (typeof Reflect === "object" && typeof Reflect.metadata === "function") return Reflect.metadata(metadataKey, metadataValue);
}

function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

class TaquitoTezosDomainsProxyContractAddressResolver {
    constructor(tezos) {
        this.tezos = tezos;
    }
    getAddress(proxyContractAddress) {
        return __awaiter(this, void 0, void 0, function* () {
            const storage = yield this.tezos.storage(proxyContractAddress);
            if (!storage || !storage.contract) {
                throw new Error(`Cannot resolve address from proxy contract ${proxyContractAddress}, because it doesn't exist or doesn't have 'contract' field in the storage.`);
            }
            return storage.contract;
        });
    }
}

let TaquitoReverseRecord = class TaquitoReverseRecord {
};
__decorate([
    encoder(BytesEncoder),
    __metadata("design:type", Object)
], TaquitoReverseRecord.prototype, "name", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], TaquitoReverseRecord.prototype, "data", void 0);
TaquitoReverseRecord = __decorate([
    RpcResponse()
], TaquitoReverseRecord);
let TaquitoDomainRecord = class TaquitoDomainRecord {
};
__decorate([
    encoder(BytesEncoder),
    __metadata("design:type", Object)
], TaquitoDomainRecord.prototype, "expiry_key", void 0);
__decorate([
    encoder(BigNumberEncoder),
    __metadata("design:type", Number)
], TaquitoDomainRecord.prototype, "level", void 0);
__decorate([
    encoder(BigNumberEncoder),
    __metadata("design:type", Number)
], TaquitoDomainRecord.prototype, "validator", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], TaquitoDomainRecord.prototype, "data", void 0);
__decorate([
    encoder(BigNumberEncoder),
    __metadata("design:type", Object)
], TaquitoDomainRecord.prototype, "tzip12_token_id", void 0);
TaquitoDomainRecord = __decorate([
    RpcResponse()
], TaquitoDomainRecord);
let TaquitoDomainInfoResponse = class TaquitoDomainInfoResponse {
};
__decorate([
    encoder(BytesEncoder),
    __metadata("design:type", String)
], TaquitoDomainInfoResponse.prototype, "name", void 0);
__decorate([
    encoder(DateEncoder),
    __metadata("design:type", Object)
], TaquitoDomainInfoResponse.prototype, "expiry", void 0);
__decorate([
    encoder(MapEncoder),
    __metadata("design:type", RecordMetadata)
], TaquitoDomainInfoResponse.prototype, "data", void 0);
TaquitoDomainInfoResponse = __decorate([
    RpcResponse()
], TaquitoDomainInfoResponse);

class TaquitoTezosDomainsResolverDataProvider {
    constructor(tezos, addressBook, tracer) {
        this.tezos = tezos;
        this.addressBook = addressBook;
        this.tracer = tracer;
    }
    resolveDomainInfo(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Getting record '${name}'.`);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry);
            const result = yield this.tezos.executeView(address, 'resolve-name', [RpcRequestData.fromValue(name, BytesEncoder).encode()]);
            const record = result.decode(TaquitoDomainInfoResponse);
            this.tracer.trace(`<= Received record.`, record);
            return record;
        });
    }
    resolveReverseRecordDomainInfo(address) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Getting reverse record '${address}'`);
            const contractAddress = yield this.addressBook.lookup(SmartContractType.NameRegistry);
            const result = yield this.tezos.executeView(contractAddress, 'resolve-address', [address]);
            const reverseRecordDomain = result.decode(TaquitoDomainInfoResponse);
            this.tracer.trace(`<= Received reverse record.`, reverseRecordDomain);
            if (!reverseRecordDomain) {
                return null;
            }
            return reverseRecordDomain;
        });
    }
}

class TaquitoTezosDomainsDataProvider {
    constructor(tezos, addressBook, tracer) {
        this.tezos = tezos;
        this.addressBook = addressBook;
        this.tracer = tracer;
    }
    getDomainRecord(name) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Getting record '${name}'.`);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry);
            const result = yield this.tezos.getBigMapValue(address, s => s.store.records, RpcRequestData.fromValue(name, BytesEncoder));
            const record = result.decode(TaquitoDomainRecord);
            this.tracer.trace(`<= Received record.`, record);
            return record;
        });
    }
    getReverseRecord(address) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Getting reverse record '${address}'`);
            const contractAddress = yield this.addressBook.lookup(SmartContractType.NameRegistry);
            const result = yield this.tezos.getBigMapValue(contractAddress, s => s.store.reverse_records, RpcRequestData.fromValue(address));
            const reverseRecord = result.decode(TaquitoReverseRecord);
            this.tracer.trace(`<= Received reverse record.`, reverseRecord);
            return reverseRecord;
        });
    }
    getDomainExpiry(key) {
        return __awaiter(this, void 0, void 0, function* () {
            this.tracer.trace(`=> Getting expiry with key '${key}'`);
            const address = yield this.addressBook.lookup(SmartContractType.NameRegistry);
            const result = yield this.tezos.getBigMapValue(address, s => s.store.expiry_map, RpcRequestData.fromValue(key, BytesEncoder));
            const expiry = result.scalar(DateEncoder);
            this.tracer.trace('<= Received expiry.', expiry);
            return expiry;
        });
    }
}

/**
 * Facade class that surfaces all of the libraries capability and allow it's configuration.
 * Uses taquito framework.
 */
class TaquitoTezosDomainsClient {
    constructor(config) {
        this._supported = true;
        if (config) {
            this.setConfig(config);
        }
    }
    /**
     * Gets the validator instance. The validator contains functions for validating domain names.
     */
    get validator() {
        return this._validator;
    }
    /**
     * Gets the manager instance. The manager contains functions for buying and updating domains and reverse records.
     */
    get manager() {
        return this._manager;
    }
    /**
     * Gets the resolver instance. The resolver contains functions for resolving names and addresses.
     */
    get resolver() {
        return this._resolver;
    }
    /** Whether this is supported instance of `TaquitoTezosDomainsClient` (as opposed to `TaquitoTezosDomainsClient.Unsupported`) */
    get isSupported() {
        return this._supported;
    }
    setConfig(config) {
        if (!this._supported) {
            throw new Error('Invalid operation. Unsupported client cannot be modified.');
        }
        this._validator = new TezosDomainsValidator(config);
        const tracer = createTracer(config);
        const tezos = new TaquitoClient(config.tezos, tracer);
        const proxyContractAddressResolver = new TaquitoTezosDomainsProxyContractAddressResolver(tezos);
        const addressBook = new AddressBook(proxyContractAddressResolver, config);
        const dataProvider = new TaquitoTezosDomainsResolverDataProvider(tezos, addressBook, tracer);
        const commitmentGenerator = new CommitmentGenerator();
        const bigMapDataProvider = new TaquitoTezosDomainsDataProvider(tezos, addressBook, tracer);
        const managerDataProvider = new TaquitoManagerDataProvider(tezos, addressBook, tracer, commitmentGenerator, this.validator, bigMapDataProvider);
        const operationFactory = new TaquitoTezosDomainsOperationFactory(tezos, addressBook, tracer, commitmentGenerator, managerDataProvider, this.validator);
        this._manager = new BlockchainDomainsManager(tezos, tracer, operationFactory, managerDataProvider);
        this._resolver = createResolver(config, dataProvider, tracer, this.validator);
    }
    /**
     * Gets a singleton instance with method that are stubbed and return null or default value, or throw an exception.
     * This instance can be used in an app that supports multiple networks where on some of them Tezos Domains are supported
     * and on other not supported (contracts are not deployed etc.).
     *
     * @example
     * ```
     * function getClient(network: string) {
     *     if(isTezosDomainsSupportedNetwork(network)) {
     *          return new TaquitoTezosDomainsClient({ network, tezos });
     *     } else {
     *          return TaquitoTezosDomainsClient.Unsupported;
     *     }
     * }
     *
     * const client = getClient('unsupportednetwork');
     *
     * if (client.isSupported) { // not executed
     *     console.log(await client.manager.getAcquisitionInfo('alice.tez'));
     * }
     *
     * await client.resolver.resolveNameToAddress('alice.tez'); // returns null
     * ```
     */
    static get Unsupported() {
        const client = new TaquitoTezosDomainsClient(null);
        client.setUnsupported();
        return client;
    }
    setUnsupported() {
        this._supported = false;
        this._manager = new UnsupportedDomainsManager();
        this._resolver = new NullNameResolver();
        this._validator = new UnsupportedDomainNameValidator();
    }
}

export { TaquitoTezosDomainsClient };
//# sourceMappingURL=taquito-client.es2015.js.map
